import { useEffect, useState } from 'react';

// Full-screen "designing" overlay shown while the engine runs. The run is
// accuracy-first (street centerlines + graph-routed cabling + drops), so it can
// take a while - this shows a live elapsed timer and the real pipeline stage so
// the wait feels intentional, not stuck.
const STAGES = [
  'استخراجِ خطِ مرکزیِ خیابان‌ها…',
  'خوشه‌بندیِ FAT و ODC…',
  'گروه‌بندیِ OLT و جانماییِ حوضچه‌ها…',
  'محاسبهٔ بهینهٔ مسیرِ کابل…',
  'اعتبارسنجیِ استاندارد و بودجهٔ نوری…',
];

const KEYFRAMES = `
@keyframes ds-spin { to { transform: rotate(360deg); } }
@keyframes ds-spin-rev { to { transform: rotate(-360deg); } }
@keyframes ds-pulse { 0%,100% { transform: scale(1); opacity: .92 } 50% { transform: scale(1.28); opacity: .5 } }
@keyframes ds-shimmer { 0% { transform: translateX(-130%) } 100% { transform: translateX(430%) } }
@keyframes ds-fade { from { opacity: 0; transform: translateY(5px) } to { opacity: 1; transform: none } }
`;

const overlay = {
  position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(2,6,23,0.55)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
  direction: 'rtl', fontFamily: "'Vazirmatn', Tahoma, sans-serif",
};
const card = {
  width: 'min(340px, 90vw)', padding: '30px 28px 24px', textAlign: 'center', borderRadius: 20,
  background: 'linear-gradient(160deg, rgba(15,23,42,0.97), rgba(20,32,52,0.97))',
  border: '1px solid rgba(45,212,191,0.25)',
  boxShadow: '0 24px 70px rgba(2,6,23,0.7), 0 0 0 1px rgba(255,255,255,0.03) inset',
  color: '#e2e8f0', animation: 'ds-fade .25s ease',
};
const ring = {
  position: 'absolute', inset: 0, borderRadius: '50%',
  background: 'conic-gradient(from 0deg, rgba(45,212,191,0) 0deg, rgba(45,212,191,.12) 210deg, #2dd4bf 340deg, #5eead4 360deg)',
  WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 7px), #000 calc(100% - 7px))',
  mask: 'radial-gradient(farthest-side, transparent calc(100% - 7px), #000 calc(100% - 7px))',
  animation: 'ds-spin 1.1s linear infinite',
};
const ringInner = {
  position: 'absolute', inset: 15, borderRadius: '50%',
  background: 'conic-gradient(from 180deg, rgba(13,148,136,0) 0deg, #0d9488 300deg, #2dd4bf 360deg)',
  WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
  mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))',
  animation: 'ds-spin-rev 1.8s linear infinite',
};
const core = {
  position: 'absolute', inset: '38%', borderRadius: '50%',
  background: 'radial-gradient(circle, #5eead4, #0d9488)',
  boxShadow: '0 0 18px rgba(45,212,191,.7)', animation: 'ds-pulse 1.4s ease-in-out infinite',
};

export default function DesignSpinner({ active }) {
  const [elapsed, setElapsed] = useState(0);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    setElapsed(0); setStage(0);
    const t0 = Date.now();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 250);
    const st = setInterval(() => setStage((s) => (s + 1) % STAGES.length), 2400);
    return () => { clearInterval(tick); clearInterval(st); };
  }, [active]);

  if (!active) return null;

  return (
    <div style={overlay}>
      <style>{KEYFRAMES}</style>
      <div style={card}>
        <div style={{ position: 'relative', width: 96, height: 96, margin: '0 auto' }}>
          <div style={ring} />
          <div style={ringInner} />
          <div style={core} />
        </div>
        <div style={{ fontWeight: 800, fontSize: 16, marginTop: 24 }}>در حال طراحیِ بهینهٔ شبکه</div>
        <div key={stage} style={{ fontSize: 12.5, color: '#5eead4', marginTop: 9, minHeight: 18, animation: 'ds-fade .4s ease' }}>
          {STAGES[stage]}
        </div>
        <div style={{ marginTop: 14 }}>
          <span style={{ fontSize: 34, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: '#f1f5f9' }}>{elapsed}</span>
          <span style={{ fontSize: 13, color: '#94a3b8', marginInlineStart: 6 }}>ثانیه</span>
        </div>
        <div style={{ position: 'relative', height: 4, borderRadius: 3, background: 'rgba(148,163,184,0.16)', overflow: 'hidden', marginTop: 16 }}>
          <div style={{ position: 'absolute', top: 0, bottom: 0, width: '30%', borderRadius: 3,
            background: 'linear-gradient(90deg, transparent, #2dd4bf, transparent)', animation: 'ds-shimmer 1.3s ease-in-out infinite' }} />
        </div>
        <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 14, lineHeight: 1.7 }}>
          به دنبالِ دقیق‌ترین طرحِ ممکن هستیم؛ این ممکن است چند لحظه طول بکشد.
        </div>
      </div>
    </div>
  );
}
